/**
 * Iter 119 — ToDoList: lista de tareas por técnico.
 *
 * Sustituye visualmente al Planificador (que se mantiene intacto en /planner
 * y en el backend). Todos los usuarios pueden crear tareas y asignarlas a
 * cualquier técnico. La trazabilidad (creador, fechas, quién completó) se
 * guarda automáticamente y no se puede editar desde la UI.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, Filter, RotateCw, Trash2, User as UserIcon, ListTodo } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_LABEL = {
  pending: "Pendiente",
  in_progress: "En proceso",
  completed: "Completada",
};

const STATUS_COLOR = {
  pending: "bg-[#F7F7F4] border-[#525252]",
  in_progress: "bg-[#FFF9E6] border-[#B8860B]",
  completed: "bg-[#F0F4E8] border-[#6E8B3D]",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function TodoList() {
  const [technicians, setTechnicians] = useState({ active: [], inactive: [] });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [defaultAssignee, setDefaultAssignee] = useState("");
  const [showCompleted, setShowCompleted] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCreator, setFilterCreator] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tR, tksR] = await Promise.all([
        api.get("/todos/technicians"),
        api.get("/todos", { params: { include_completed: showCompleted } }),
      ]);
      setTechnicians(tR.data || { active: [], inactive: [] });
      setTasks(tksR.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [showCompleted]);

  const allUsers = useMemo(() => (
    [...technicians.active, ...technicians.inactive]
  ), [technicians]);

  const groupedByTech = useMemo(() => {
    const map = new Map();
    // Semilla con TODOS los técnicos activos (aparecerán vacíos si aún no
    // tienen tareas) — regla iter 119.
    [...technicians.active, ...technicians.inactive].forEach((u) => {
      map.set(u.id, { user: u, items: [] });
    });
    tasks.forEach((t) => {
      if (filterStatus && t.status !== filterStatus) return;
      if (filterCreator && t.created_by_id !== filterCreator) return;
      if (!map.has(t.assigned_to_id)) {
        // Técnico ya no existe (hard-deleted). Grupo huérfano visible con nombre snapshot.
        map.set(t.assigned_to_id, {
          user: { id: t.assigned_to_id, name: t.assigned_to_name || "(usuario eliminado)", active: false },
          items: [],
        });
      }
      map.get(t.assigned_to_id).items.push(t);
    });
    return Array.from(map.values());
  }, [tasks, technicians, filterStatus, filterCreator]);

  const openNewFor = (userId) => {
    setDefaultAssignee(userId || (technicians.active[0]?.id) || "");
    setShowNew(true);
  };

  const updateTask = async (task, changes) => {
    try {
      const r = await api.put(`/todos/${task.id}`, changes);
      setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`¿Eliminar la tarea "${task.text}"?`)) return;
    try {
      await api.delete(`/todos/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      toast.success("Tarea eliminada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createTask = async (payload) => {
    try {
      const r = await api.post("/todos", payload);
      setTasks((prev) => [r.data, ...prev]);
      setShowNew(false);
      toast.success("Tarea creada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-6 lg:py-8" data-testid="todolist-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3 border-b border-dashed border-[#D5D7CE] pb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Tareas por técnico
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight flex items-center gap-3">
            <span className="ds-ic-tile" style={{ width: 40, height: 40, borderRadius: 10 }}><ListTodo size={20}/></span>
            ToDoList
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadAll}
            data-testid="todolist-refresh"
            className="ds-btn"
          >
            <RotateCw size={13} /> Refrescar
          </button>
          <button
            onClick={() => openNewFor("")}
            data-testid="todolist-new"
            className="ds-btn primary"
          >
            <Plus size={13} /> Nueva tarea
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex items-center gap-3 flex-wrap border border-[#E5E5E0] bg-[#F7F7F4] px-3 py-2">
        <Filter size={12} className="text-[#525252]" />
        <label className="text-xs flex items-center gap-1.5">
          <span className="text-[#525252]">Estado:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="todolist-filter-status"
            className="border border-[#E5E5E0] bg-white px-2 py-1 text-xs"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_progress">En proceso</option>
            <option value="completed">Completada</option>
          </select>
        </label>
        <label className="text-xs flex items-center gap-1.5">
          <span className="text-[#525252]">Creado por:</span>
          <select
            value={filterCreator}
            onChange={(e) => setFilterCreator(e.target.value)}
            data-testid="todolist-filter-creator"
            className="border border-[#E5E5E0] bg-white px-2 py-1 text-xs"
          >
            <option value="">Todos</option>
            {allUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            data-testid="todolist-toggle-completed"
          />
          <span className="text-[#525252]">Mostrar completadas</span>
        </label>
      </div>

      {loading ? (
        <div className="p-8 text-sm text-[#525252] text-center">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {groupedByTech.map(({ user, items }) => (
            <div
              key={user.id}
              className="border border-[#E5E5E0] bg-white flex flex-col"
              data-testid={`todolist-col-${user.id}`}
            >
              <div className="px-3 py-2 border-b border-[#E5E5E0] bg-[#F7F7F4] flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <UserIcon size={12} />
                    {user.name}
                    {user.active === false && (
                      <span className="text-[9px] uppercase tracking-widest text-[#525252] border border-[#525252] px-1">Desactivado</span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-[#525252]">
                    {items.length} tarea{items.length === 1 ? "" : "s"}
                  </div>
                </div>
                <button
                  onClick={() => openNewFor(user.id)}
                  data-testid={`todolist-add-${user.id}`}
                  className="p-1 hover:bg-white"
                  title={`Añadir tarea a ${user.name}`}
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="p-2 flex-1 space-y-2 min-h-[100px] max-h-[600px] overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-[11px] text-[#525252] italic p-2 text-center">
                    Sin tareas.
                  </div>
                ) : items.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    users={allUsers}
                    onUpdate={(changes) => updateTask(t, changes)}
                    onDelete={() => deleteTask(t)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewTaskDialog
          users={technicians.active}
          defaultAssignee={defaultAssignee}
          onSave={createTask}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

function TaskCard({ task, users, onUpdate, onDelete }) {
  const isCompleted = task.status === "completed";
  return (
    <div
      className={`border-l-4 border ${STATUS_COLOR[task.status]} px-2 py-1.5 text-xs`}
      data-testid={`todolist-task-${task.id}`}
    >
      <div className={`font-semibold ${isCompleted ? "line-through text-[#525252]" : ""}`}>
        {task.text}
      </div>
      {task.notes && (
        <div className="text-[10px] text-[#525252] mt-0.5 whitespace-pre-wrap">{task.notes}</div>
      )}
      <div className="mt-1 space-y-0.5 text-[9px] font-mono text-[#525252]">
        <div>Asignado a: <b>{task.assigned_to_name}</b></div>
        <div>Creado por: <b>{task.created_by_name}</b> · {fmtDateTime(task.created_at)}</div>
        {isCompleted && (
          <div>Completado por: <b>{task.completed_by_name}</b> · {fmtDateTime(task.completed_at)}</div>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        <select
          value={task.status}
          onChange={(e) => onUpdate({ status: e.target.value })}
          data-testid={`todolist-task-status-${task.id}`}
          className="border border-[#E5E5E0] bg-white px-1 py-0.5 text-[10px]"
        >
          <option value="pending">{STATUS_LABEL.pending}</option>
          <option value="in_progress">{STATUS_LABEL.in_progress}</option>
          <option value="completed">{STATUS_LABEL.completed}</option>
        </select>
        <select
          value={task.assigned_to_id}
          onChange={(e) => onUpdate({ assigned_to_id: e.target.value })}
          data-testid={`todolist-task-assignee-${task.id}`}
          className="border border-[#E5E5E0] bg-white px-1 py-0.5 text-[10px]"
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        {!isCompleted && (
          <button
            onClick={() => onUpdate({ status: "completed" })}
            data-testid={`todolist-task-complete-${task.id}`}
            className="ml-auto p-0.5 hover:bg-[#F0F4E8] text-[#6E8B3D]"
            title="Marcar completada"
          >
            <Check size={12} />
          </button>
        )}
        <button
          onClick={onDelete}
          data-testid={`todolist-task-delete-${task.id}`}
          className="p-0.5 hover:bg-red-50 text-red-700"
          title="Eliminar"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

function NewTaskDialog({ users, defaultAssignee, onSave, onClose }) {
  const [assignee, setAssignee] = useState(defaultAssignee || users[0]?.id || "");
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");

  const submit = () => {
    if (!text.trim()) {
      toast.error("Escribe el texto de la tarea");
      return;
    }
    if (!assignee) {
      toast.error("Selecciona un técnico");
      return;
    }
    onSave({ assigned_to_id: assignee, text: text.trim(), notes: notes.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md border border-[#111110]" onClick={(e) => e.stopPropagation()} data-testid="todolist-new-dialog">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-mono text-[10px] uppercase tracking-widest">Nueva tarea</div>
          <button onClick={onClose} className="hover:bg-[#F7F7F4] p-1"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Asignar a
            </label>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              data-testid="todolist-new-assignee"
              className="w-full border border-[#E5E5E0] px-3 py-2 text-sm bg-white"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Tarea *
            </label>
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="p. ej. Cambiar batería MacBook Pro"
              data-testid="todolist-new-text"
              className="w-full border border-[#E5E5E0] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="todolist-new-notes"
              className="w-full border border-[#E5E5E0] px-3 py-2 text-sm resize-y"
            />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-[#E5E5E0] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs uppercase tracking-widest text-[#525252] hover:bg-[#F7F7F4]">
            Cancelar
          </button>
          <button onClick={submit} data-testid="todolist-new-save" className="px-4 py-1.5 text-xs uppercase tracking-widest bg-[#6E8B3D] text-white hover:bg-[#556C2E]">
            Crear tarea
          </button>
        </div>
      </div>
    </div>
  );
}
